'use client'
import React, { useCallback, useState } from 'react'
import type { AccordionItemBlockProps } from '@/blocks/AccordionItemBlock'
import { AccordionItemBlock } from '@/blocks/AccordionItemBlock'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import type { ExpectedBlockType, SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { filterByBlockType } from '@/services/storyblok/Storyblok.helpers'
import { storyblokEditable } from '@storyblok/react'
import { Accordion } from 'ui/src/components/Accordion/Accordion'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { renderRichTextToString } from './RichTextBlock/richTextReactRenderer'
import { accordion, textContent, wrapper } from './AccordionBlock.css'

type Props = SbBaseBlockProps<{
  items: ExpectedBlockType<AccordionItemBlockProps>
  title?: string
  description?: string
  isFAQ?: boolean
  centerLayout?: boolean
}>

export function AccordionBlock({ blok, nested }: Props) {
  const [openItem, setOpenItem] = useState<string>()

  const accordionItems = filterByBlockType(blok.items, AccordionItemBlock.blockName)
  const enableFAQStructuredData = blok.isFAQ ?? false
  const displayTitleDescriptionSection = blok.title || blok.description
  const isCentered = blok.centerLayout || !displayTitleDescriptionSection
  const FAQStructuredData = getFAQStructuredData(accordionItems)

  const handleValueChange = useCallback((value: string) => {
    setOpenItem(value)
  }, [])

  let content: React.ReactNode = null
  if (nested) {
    content = (
      <div className={yStack({ gap: { _: 'md', lg: 'lg' } })}>
        {displayTitleDescriptionSection && <AccordionTitleDescription blok={blok} />}
        <Accordion.Root
          className={accordion}
          type="single"
          collapsible
          value={openItem}
          onValueChange={handleValueChange}
        >
          {accordionItems.map((nestedBlock) => (
            <AccordionItemBlock key={nestedBlock._uid} blok={nestedBlock} openItem={openItem} />
          ))}
        </Accordion.Root>
      </div>
    )
  } else {
    content = (
      <GridLayout.Root className={wrapper} {...storyblokEditable(blok)}>
        {displayTitleDescriptionSection && (
          <GridLayout.Content width="1/2" align={isCentered ? 'center' : 'left'}>
            <AccordionTitleDescription blok={blok} />
          </GridLayout.Content>
        )}
        <GridLayout.Content width="1/2" align={isCentered ? 'center' : 'right'}>
          <Accordion.Root type="single" collapsible value={openItem} onValueChange={handleValueChange}>
            {accordionItems.map((nestedBlock) => (
              <AccordionItemBlock key={nestedBlock._uid} blok={nestedBlock} openItem={openItem} />
            ))}
          </Accordion.Root>
        </GridLayout.Content>
      </GridLayout.Root>
    )
  }

  return (
    <>
      {enableFAQStructuredData && (
        <script
          key="accordion-faq-sctructured-data"
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(FAQStructuredData),
          }}
        />
      )}
      {content}
    </>
  )
}

export function AccordionTitleDescription({ blok }: { blok: Props['blok'] }) {
  return (
    <div className={textContent}>
      {blok.title && (
        <Heading as="h2" variant={{ _: 'standard.24', ...(!blok.centerLayout && { md: 'standard.32' }) }}>
          {blok.title}
        </Heading>
      )}
      {blok.description && (
        <Text color="textSecondary" size={{ _: 'xl', ...(!blok.centerLayout && { md: 'xxl' }) }}>
          {blok.description}
        </Text>
      )}
    </div>
  )
}

const getFAQStructuredData = (accordions: ReadonlyArray<Pick<AccordionItemBlockProps['blok'], 'title' | 'body'>>) => {
  return {
    '@context': 'http://schema.org',
    '@type': 'FAQPage',
    mainEntity: accordions.map((item) => ({
      '@type': 'Question',
      name: item.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: renderRichTextToString(item.body),
      },
    })),
  }
}
